import { FC, useCallback, useEffect, useState } from 'react';
import { getEasyLayout, sendServiceRequest } from '@resistdesign/voltra/app/utils';
import { UserManager } from 'oidc-react';
import styled from 'styled-components';
import { Profile } from '../../common/Types';
import { SIGN_UP_CONFIG } from '../../common/SignUp';
import { getAdminInfo } from '../../common/utils/AdminUtils';
import { APP_ENV_CONFIG } from '../config';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;
const {
  EMPLOYER: {
    PROVISIONING: { POLL_INTERVAL_MS, TIMEOUT_MS },
  },
} = SIGN_UP_CONFIG;

const LayoutBase = styled.div`
  width: 100%;
  height: 100%;
`;
const AreaBase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const {
  layout: Layout,
  areas: { CenterContent },
} = getEasyLayout(LayoutBase, AreaBase)`
  center-content, 1fr
  \\ 1fr
`;
const Card = styled.section`
  width: min(100%, 36em);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1em;
  text-align: center;
`;
const Progress = styled.progress`
  width: 100%;
  margin: 0;
`;
const TryAgainButton = styled.button`
  font-size: 1.1em;
  padding: 0.9em 1.2em;
`;

const wait = (delay: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, delay));

export type EmployerPaymentCompleteProps = {
  authorization: string;
  userManager: UserManager;
};

export const EmployerPaymentComplete: FC<EmployerPaymentCompleteProps> = ({ authorization, userManager }) => {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const onTryAgain = useCallback(() => {
    setFailed(false);
    setAttempt((currentAttempt) => currentAttempt + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (authorization) {
      const startedAt = Date.now();

      const finishSetup = async (): Promise<void> => {
        let completed = false;

        while (!cancelled && !completed && Date.now() - startedAt < TIMEOUT_MS) {
          try {
            const profile = (await sendServiceRequest(
              {
                protocol: 'https',
                domain: API_DOMAIN,
                basePath: '/user/profile',
                authorization,
              },
              '/read'
            )) as Profile | undefined;

            if (profile?.groupAdminIdCode) {
              const refreshedUser = await userManager.signinSilent();

              if (!refreshedUser) {
                throw new Error('SESSION_REFRESH_FAILED');
              }

              const { ['cognito:groups']: groups = [] } = refreshedUser.profile as any;
              const { isGroupAdmin } = getAdminInfo(groups);

              if (isGroupAdmin && !cancelled) {
                history.pushState({}, '', '/admin');
                completed = true;
              }
            }
          } catch (error) {
            // Keep polling until timeout so temporary API or session refresh failures can recover.
          }

          if (!cancelled && !completed) {
            await wait(POLL_INTERVAL_MS);
          }
        }

        if (!cancelled && !completed) {
          setFailed(true);
        }
      };

      void finishSetup();
    }

    return () => {
      cancelled = true;
    };
  }, [authorization, userManager, attempt]);

  return (
    <Layout>
      <CenterContent>
        <Card>
          {failed ? (
            <>
              <h2>We're having trouble finishing your account setup.</h2>
              <p>
                Your payment may have completed successfully. Please try again. If this continues, contact support
                through <a href="https://advisorinterview.com/">AdvisorInterview.com</a>.
              </p>
              <TryAgainButton className="primary" onClick={onTryAgain}>
                Try Again
              </TryAgainButton>
            </>
          ) : (
            <>
              <h2>Setting up your Employer account...</h2>
              <p>Your payment was successful. We're finishing your account setup.</p>
              <Progress />
            </>
          )}
        </Card>
      </CenterContent>
    </Layout>
  );
};
